import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  Paper,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { 
  Add, 
  WhatsApp, 
  Close, 
  CheckCircle,
  NavigateNext 
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { getProductById, formatPrice } from '../data/products';
import { useQuote } from '../context/QuoteContext';
import SEO from "../components/SEO";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = getProductById(id);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const { addToQuote } = useQuote();

  useEffect(() => {
    if (!product) {
      navigate('/products');
    }
  }, [product, navigate]);

  if (!product) {
    return null;
  }

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Products", url: "/products" },
    { name: product.categoryName, url: `/products?category=${product.categoryId}` },
    { name: product.name, url: `/product/${product.id}` }
  ];

  const handleAddToQuote = () => {
    setSelectedProduct(product);
    setQuantity(product.minOrder.toString());
    setNotes('');
    setOpenDialog(true);
  };

  const handleConfirmAddToQuote = () => {
    if (selectedProduct && quantity) {
      addToQuote({
        productId: selectedProduct.id,
        name: selectedProduct.name,
        price: selectedProduct.price,
        unit: selectedProduct.unit,
        quantity: parseInt(quantity),
        notes: notes,
        categoryName: selectedProduct.categoryName
      });
      setOpenDialog(false);
      setSelectedProduct(null);
    }
  };

  const handleWhatsApp = () => {
    const message = `Hi, I'm interested in ${product.name}. Could you please provide more details?`;
    const whatsappUrl = `https://wa.me/919764166071?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <>
      <SEO
        title={`${product.name} - ${product.categoryName}`}
        description={product.description}
        keywords={`${product.name}, ${product.categoryName}, ${product.specifications?.material || ''}`}
        image={product.image}
        url={`/product/${product.id}`}
        type="product"
        productData={product}
        breadcrumbs={breadcrumbs}
        price={product.price}
      />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumbs 
          separator={<NavigateNext fontSize="small" />} 
          sx={{ mb: 3 }}
        >
          <Link component={RouterLink} to="/" underline="hover">
            Home
          </Link>
          <Link component={RouterLink} to="/products" underline="hover">
            Products
          </Link>
          <Link 
            component={RouterLink} 
            to={`/products?category=${product.categoryId}`} 
            underline="hover"
          >
            {product.categoryName}
          </Link>
          <Typography color="text.primary">{product.name}</Typography>
        </Breadcrumbs>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Grid container spacing={6}>
            {/* Product Image */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={3}
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  height: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Box
                  component="img"
                  src={product.image || "/assets/products/placeholder.jpg"}
                  alt={product.name}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div style="font-size: 4rem; color: #ccc;">📦</div>';
                  }}
                />
              </Paper>
            </Grid>

            {/* Product Info */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Chip 
                  label={product.categoryName} 
                  color="secondary" 
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start', mb: 2 }}
                />
                
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                  {product.name}
                </Typography>
                
                <Typography variant="h4" color="primary" sx={{ fontWeight: 700, mb: 3 }}>
                  {formatPrice(product.price)}
                  <Typography component="span" variant="h6" color="text.secondary">
                    /{product.unit}
                  </Typography>
                </Typography>

                {/* Product Meta */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Minimum Order
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {product.minOrder} {product.unit}
                      </Typography>
                    </Grid>
                    {product.size && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Size
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {product.size}
                        </Typography>
                      </Grid>
                    )}
                    {product.color && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Color
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {product.color}
                        </Typography>
                      </Grid>
                    )}
                    {product.material && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Material
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {product.material}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                {/* Description */}
                <Typography variant="body1" paragraph sx={{ flexGrow: 1, lineHeight: 1.6 }}>
                  {product.description}
                </Typography>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 'auto' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleAddToQuote}
                    startIcon={<Add />}
                    fullWidth
                  >
                    Add to Quote
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleWhatsApp}
                    startIcon={<WhatsApp />}
                    fullWidth
                    sx={{ 
                      backgroundColor: '#25D366',
                      '&:hover': {
                        backgroundColor: '#1EA952',
                      }
                    }}
                  >
                    WhatsApp Inquiry
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/contact"
                    variant="outlined"
                    size="large"
                    fullWidth
                  >
                    Contact Us
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Features and Specifications */}
          <Grid container spacing={4} sx={{ mt: 4 }}>
            {/* Features */}
            {product.features && product.features.length > 0 && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Key Features
                  </Typography>
                  <List>
                    {product.features.map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}

            {/* Specifications */}
            {product.specifications && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, color: 'primary.main' }}>
                    Specifications
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 800, textTransform: 'capitalize' }}>
                          {key}:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {Array.isArray(value) ? value.join(', ') : value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </motion.div>

        {/* Add to Quote Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Add to Quote
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedProduct && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedProduct.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {formatPrice(selectedProduct.price)} per {selectedProduct.unit} (Min Order: {selectedProduct.minOrder})
                </Typography>
                
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  margin="normal"
                  inputProps={{ min: selectedProduct.minOrder }}
                  required
                />
                
                <TextField
                  fullWidth
                  label="Notes (Optional)"
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  margin="normal"
                  placeholder="Add any specific requirements or notes..."
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmAddToQuote} variant="contained">
              Add to Quote
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default ProductDetail;
